import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { LineChart, Target, TrendingUp } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { GlassSurface, GlowCard } from '@/components/glow-card';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { DetailError } from '@/components/detail-page';
import { fadeUp, stagger } from '@/components/motion';
import { useForecast } from '@/features/forecasts/use-forecast';
import {
  useForecastScenarios,
  useForecastSensitivities,
} from '@/features/forecasts/use-forecast-scenarios';
import { useCampaign } from '@/features/campaigns/use-campaign';
import { cn } from '@/lib/cn';

interface ScenarioRow {
  id: string;
  name: 'do_nothing' | 'execute_current_plan' | 'accelerated';
  label: string;
  horizon_value: number;
  horizon_low: number;
  horizon_high: number;
  uplift_fraction: number;
  points: Array<{ day_offset: number; value: number; low: number; high: number }>;
  rationale: string | null;
}

const SCENARIO_COLOR: Record<string, string> = {
  do_nothing: 'stroke-muted-foreground/60 fill-muted-foreground/10',
  execute_current_plan: 'stroke-primary fill-primary/15',
  accelerated: 'stroke-emerald-500 fill-emerald-500/15',
};

export function ForecastDetailRoute(): JSX.Element {
  const { forecastId } = useParams({ strict: false }) as { forecastId: string };
  const forecast = useForecast(forecastId);
  const scenarios = useForecastScenarios(forecastId);
  const sensitivities = useForecastSensitivities(forecastId);

  const f = forecast.data;
  const boundCampaign = useCampaign(f?.campaign_id ?? '');
  const items = (scenarios.data?.items ?? []) as ScenarioRow[];

  // Build a tiny inline SVG chart. Chart libs would be overkill for one chart;
  // points are sparse (≤90).
  const chart = useMemo(() => {
    if (items.length === 0) return null;
    const maxY = Math.max(
      ...items.flatMap((s) => s.points.map((p) => p.high)),
      0,
    );
    const minY = 0;
    const maxX = Math.max(
      ...items.flatMap((s) => s.points.map((p) => p.day_offset)),
      1,
    );
    const W = 720;
    const H = 240;
    const PAD = 28;
    const fx = (x: number): number => PAD + ((W - 2 * PAD) * x) / maxX;
    const fy = (y: number): number => H - PAD - ((H - 2 * PAD) * (y - minY)) / (maxY - minY || 1);

    const polylineFor = (pts: ScenarioRow['points']) =>
      pts.map((p) => `${fx(p.day_offset)},${fy(p.value)}`).join(' ');
    const bandFor = (pts: ScenarioRow['points']) => {
      const top = pts.map((p) => `${fx(p.day_offset)},${fy(p.high)}`).join(' ');
      const bottom = pts
        .slice()
        .reverse()
        .map((p) => `${fx(p.day_offset)},${fy(p.low)}`)
        .join(' ');
      return `${top} ${bottom}`;
    };

    return {
      maxX,
      maxY,
      W,
      H,
      PAD,
      polylineFor,
      bandFor,
    };
  }, [items]);

  // Error is terminal — render it INSTEAD of the loading spinner (the old
  // code showed an error banner next to a spinner that never resolved).
  if (forecast.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={forecast.error}
          entityKind="forecast"
          backTo="/forecasts"
          backLabel="Forecasts"
        />
      </div>
    );
  }

  return (
    <motion.div className="space-y-6" initial="hidden" animate="show" variants={stagger}>
      <PageHeader
        title={f ? `${f.horizon_days}-day forecast` : 'Forecast'}
        eyebrow="Forecast"
        description={
          f
            ? `Created ${new Date(f.created_at).toLocaleDateString()} — scenario range for the next ${f.horizon_days} days.`
            : '...'
        }
        actions={f ? <StatusPill status={f.status} /> : null}
      />

      {f?.campaign_id ? (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Target className="h-3.5 w-3.5" />
          Based on plan:{' '}
          {boundCampaign.data ? (
            <>
              <Link
                to="/campaigns/$campaignId"
                params={{ campaignId: f.campaign_id }}
                className="font-medium text-foreground hover:underline"
              >
                {boundCampaign.data.goal.length > 80
                  ? `${boundCampaign.data.goal.slice(0, 77)}…`
                  : boundCampaign.data.goal}
              </Link>
              <StatusPill status={boundCampaign.data.status} />
            </>
          ) : (
            // Keep the link usable while the campaign name loads instead of
            // having it pop in and shift the layout.
            <Link
              to="/campaigns/$campaignId"
              params={{ campaignId: f.campaign_id }}
              className="font-medium text-foreground hover:underline"
            >
              View campaign
            </Link>
          )}
        </div>
      ) : null}

      {!f ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : (
        <>
          <motion.section variants={fadeUp} className="grid gap-3 sm:grid-cols-3">
            {items.length === 0 ? (
              <GlassSurface className="p-6 text-sm text-muted-foreground sm:col-span-3">
                Computing scenarios — refresh in a moment.
              </GlassSurface>
            ) : (
              items.map((s) => (
                <GlowCard key={s.id} className="p-4">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {s.label}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <div className="text-2xl font-semibold">{s.horizon_value.toFixed(0)}</div>
                    <div className="text-xs text-muted-foreground">
                      ({s.horizon_low.toFixed(0)}–{s.horizon_high.toFixed(0)})
                    </div>
                  </div>
                  <div
                    className={cn(
                      'mt-1 text-xs',
                      s.uplift_fraction > 0
                        ? 'text-emerald-500'
                        : 'text-muted-foreground',
                    )}
                  >
                    {s.uplift_fraction > 0 ? '+' : ''}
                    {(s.uplift_fraction * 100).toFixed(1)}% at horizon
                  </div>
                </GlowCard>
              ))
            )}
          </motion.section>

          {chart && items.length > 0 ? (
            <motion.section variants={fadeUp}>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <LineChart className="h-4 w-4" /> Scenario projections
              </div>
              <GlassSurface className="overflow-x-auto p-4">
                <svg
                  viewBox={`0 0 ${chart.W} ${chart.H}`}
                  className="h-[260px] w-full text-foreground"
                >
                  {/* axes */}
                  <line
                    x1={chart.PAD}
                    y1={chart.H - chart.PAD}
                    x2={chart.W - chart.PAD}
                    y2={chart.H - chart.PAD}
                    className="stroke-border"
                  />
                  <line
                    x1={chart.PAD}
                    y1={chart.PAD}
                    x2={chart.PAD}
                    y2={chart.H - chart.PAD}
                    className="stroke-border"
                  />
                  {items.map((s) => (
                    <g key={s.id} className={SCENARIO_COLOR[s.name] ?? ''}>
                      <polygon points={chart.bandFor(s.points)} strokeWidth={0} fillOpacity={0.5} />
                      <polyline
                        points={chart.polylineFor(s.points)}
                        fill="none"
                        strokeWidth={2}
                      />
                    </g>
                  ))}
                  {/* labels */}
                  <text
                    x={chart.PAD}
                    y={chart.PAD - 8}
                    className="fill-muted-foreground text-[10px]"
                  >
                    value
                  </text>
                  <text
                    x={chart.W - chart.PAD - 10}
                    y={chart.H - chart.PAD + 16}
                    className="fill-muted-foreground text-[10px]"
                  >
                    +{chart.maxX} d
                  </text>
                </svg>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {items.map((s) => (
                    <span key={s.id} className="flex items-center gap-2">
                      <span
                        className={cn(
                          'inline-block h-2 w-3 rounded-sm',
                          s.name === 'do_nothing' && 'bg-muted-foreground/60',
                          s.name === 'execute_current_plan' && 'bg-primary',
                          s.name === 'accelerated' && 'bg-emerald-500',
                        )}
                      />
                      {s.label}
                    </span>
                  ))}
                </div>
              </GlassSurface>
            </motion.section>
          ) : null}

          {f.narrative_md ? (
            <motion.section variants={fadeUp}>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <TrendingUp className="h-4 w-4" /> Narrative
              </div>
              <GlassSurface className="p-4">
                <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
                  {f.narrative_md}
                </pre>
              </GlassSurface>
            </motion.section>
          ) : null}

          {sensitivities.data && (sensitivities.data.items as unknown[]).length > 0 ? (
            <motion.section variants={fadeUp}>
              <div className="mb-2 text-sm font-semibold">Sensitivity</div>
              <GlassSurface className="p-2">
                <ul className="space-y-1">
                  {(sensitivities.data.items as Array<{
                    id: string;
                    action_label: string;
                    contribution_fraction: number;
                    rationale: string | null;
                  }>).map((row) => (
                    <li
                      key={row.id}
                      className="flex items-start justify-between gap-3 rounded-md border border-border/60 bg-background/40 p-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">{row.action_label}</div>
                        {row.rationale ? (
                          <div className="text-xs text-muted-foreground">{row.rationale}</div>
                        ) : null}
                      </div>
                      <div className="text-sm font-mono text-emerald-500">
                        +{(row.contribution_fraction * 100).toFixed(1)}%
                      </div>
                    </li>
                  ))}
                </ul>
              </GlassSurface>
            </motion.section>
          ) : null}
        </>
      )}
    </motion.div>
  );
}
